import type { Lang, SiteCopy } from '../i18n/types';
import {
  ugAnnotatePanel,
  ugCapturePanel,
  ugCloudPanel,
  ugContrastNowSvg,
  ugContrastPastSvg,
  ugLivePanel,
  ugReviewPanel,
  ugSimPanel,
} from '../illustrations/unite-go';

export const UNITE_GO_PATH_EN = '/solutions/unite-go';
export const UNITE_GO_PATH_ZH = '/zh/solutions/unite-go';

export function uniteGoPath(lang: Lang): string {
  return lang === 'zh' ? UNITE_GO_PATH_ZH : UNITE_GO_PATH_EN;
}

export const UNITE_GO_META = {
  zh: {
    title: 'UniteGo · 极简具身数据采集',
    description:
      '用一部 iPhone 跑通具身智能从采集到仿真的高质量数据闭环。零穿戴、帧级同步、AI 标注与机械臂仿真验证。',
    keywords: 'UniteGo,具身智能,数据采集,第一视角采集,AI标注,动作仿真,iPhone,手部轨迹,UniteStream',
  },
  en: {
    title: 'UniteGo · Minimal embodied data capture',
    description:
      'One iPhone for a full embodied-AI pipeline — from natural capture to AI labeling and arm simulation, with zero wearables and frame-accurate sync.',
    keywords: 'UniteGo,embodied AI,data capture,egocentric capture,AI annotation,motion simulation,iPhone,hand tracking,UniteStream',
  },
} as const;

type ContrastItem = { text: string };
type Pillar = { title: string; body: string; panel: () => string };
type Extension = { title: string; body: string; panel: () => string };

type Copy = {
  heroTitle: string;
  heroSubtitle: string;
  ctaPrimary: string;
  ctaSecondary: string;
  contrastPastLabel: string;
  contrastPastTitle: string;
  contrastPastItems: ContrastItem[];
  contrastNowLabel: string;
  contrastNowTitle: string;
  contrastNowItems: ContrastItem[];
  pillarsEyebrow: string;
  pillarsTitle: string;
  pillars: Pillar[];
  extensionsEyebrow: string;
  extensionsTitle: string;
  extensions: Extension[];
  closingTitle: string;
  closingSubtitle: string;
  closingCta: string;
};

const COPY: Record<Lang, Copy> = {
  zh: {
    heroTitle: '极简采集，自然发生。',
    heroSubtitle:
      '用一部 iPhone，跑通具身智能从采集到仿真的高质量数据闭环。零穿戴负担，画面与 3D 手部轨迹天然帧级同步。',
    ctaPrimary: '免费预约演示',
    ctaSecondary: '查看完整工作流',
    contrastPastLabel: '过去',
    contrastPastTitle: '繁重、昂贵、散乱',
    contrastPastItems: [
      { text: '依赖昂贵的动捕头盔与数据手套。' },
      { text: '穿戴繁琐，严重破坏真实操作手感。' },
      { text: '视频与轨迹分离，人工对齐耗时费力。' },
    ],
    contrastNowLabel: 'UniteGo',
    contrastNowTitle: '轻巧、自然、同步',
    contrastNowItems: [
      { text: '仅需一部日常的 iPhone 即可开录。' },
      { text: '零穿戴负担，还原最真实的物理互动。' },
      { text: '高清画面与手部 3D 轨迹天然帧级同步。' },
    ],
    pillarsEyebrow: '核心四步',
    pillarsTitle: '一条顺畅的采集管线',
    pillars: [
      {
        title: '现场采集：零穿戴，自然操作',
        body: '拿着 iPhone 直接录。无需头盔或手套，手部动作与第一视角画面在同一时间轴上锁定，现场手感不被打断。',
        panel: ugCapturePanel,
      },
      {
        title: '自动上云：录制即沉淀',
        body: '告别散乱文件。每次采集自动上云并生成结构化数据卡片，管理员随时检索、预览与追溯来源。',
        panel: ugCloudPanel,
      },
      {
        title: 'AI 标注：切第一刀，人工精修',
        body: 'AI 自动切分连贯动作边界；标注员对照同屏 3D 轨迹微调，审核流把关，标注速度跟上采集节奏。',
        panel: ugAnnotatePanel,
      },
      {
        title: '仿真验证：人手映射机械臂',
        body: '将人手轨迹转化为机器语言。左看真手画面，右看机械臂同步演算，提前拦截运动死角，绝不让无效数据入库。',
        panel: ugSimPanel,
      },
    ],
    extensionsEyebrow: '扩展能力',
    extensionsTitle: '实时监看与质量审核',
    extensions: [
      {
        title: '云端实时监看',
        body: '采集端开录的同时，后方团队在 Web 控制台实时监看，确保工具不漏拍、动作不出画。',
        panel: ugLivePanel,
      },
      {
        title: '审核与质检流',
        body: '结构化审核队列，状态一目了然。Completed / In progress 流转清晰，标注与复核高效协作。',
        panel: ugReviewPanel,
      },
    ],
    closingTitle: '教机器人干活，从此变成轻松的日常。',
    closingSubtitle: '抛弃沉重设备，体验门槛最低的具身数据管线。',
    closingCta: '开始构建你的具身数据集',
  },
  en: {
    heroTitle: 'Capture simply. Happens naturally.',
    heroSubtitle:
      'One iPhone for a high-quality embodied-AI loop — zero wearables, frame-locked video and 3D hand tracks from field capture to simulation.',
    ctaPrimary: 'Book a free demo',
    ctaSecondary: 'See the full workflow',
    contrastPastLabel: 'Before',
    contrastPastTitle: 'Heavy, costly, fragmented',
    contrastPastItems: [
      { text: 'Expensive mocap helmets and data gloves required.' },
      { text: 'Cumbersome wearables break natural hand feel.' },
      { text: 'Video and trajectories split — manual alignment eats hours.' },
    ],
    contrastNowLabel: 'UniteGo',
    contrastNowTitle: 'Light, natural, in sync',
    contrastNowItems: [
      { text: 'Start recording with an everyday iPhone.' },
      { text: 'Zero wearables — preserve real physical interaction.' },
      { text: 'HD video and 3D hand tracks stay frame-locked by default.' },
    ],
    pillarsEyebrow: 'Four pillars',
    pillarsTitle: 'One smooth capture pipeline',
    pillars: [
      {
        title: 'Field capture: zero wearables',
        body: 'Record with a phone in hand. No helmet or gloves — egocentric video and hand motion stay locked on one timeline.',
        panel: ugCapturePanel,
      },
      {
        title: 'Auto cloud sync: every session becomes an asset',
        body: 'No scattered files. Each capture uploads automatically as a structured data card — searchable and traceable.',
        panel: ugCloudPanel,
      },
      {
        title: 'AI labeling: AI cuts first, humans refine',
        body: 'AI segments action boundaries; labelers fine-tune against synchronized 3D tracks with a review flow that keeps pace.',
        panel: ugAnnotatePanel,
      },
      {
        title: 'Simulation: hand motion to robot arm',
        body: 'Map human motion to robot commands. Real hand on the left, simulated arm on the right — catch dead zones before bad data ships.',
        panel: ugSimPanel,
      },
    ],
    extensionsEyebrow: 'Extensions',
    extensionsTitle: 'Live monitoring and review',
    extensions: [
      {
        title: 'Live cloud monitoring',
        body: 'While capture runs on site, your team watches live in the web console — nothing missed, nothing out of frame.',
        panel: ugLivePanel,
      },
      {
        title: 'Review and QA flow',
        body: 'Structured review queues with clear status. Completed and in-progress flows keep labeling and QA aligned.',
        panel: ugReviewPanel,
      },
    ],
    closingTitle: 'Teaching robots can feel like a normal day.',
    closingSubtitle: 'Drop the heavy gear. The lowest-friction embodied data pipeline.',
    closingCta: 'Start building your embodied dataset',
  },
};

export function renderUniteGoPage(
  _siteCopy: SiteCopy,
  lang: Lang,
  esc: (s: string) => string,
): string {
  const c = COPY[lang];

  const pillarRows = c.pillars
    .map(
      (p, i) => `
      <article class="ub-pillar reveal${i === 0 ? ' is-active' : ''}${i % 2 ? ' ub-pillar--reverse' : ''}" data-pillar="${i}" style="transition-delay:${i * 60}ms">
        <div class="ub-pillar__copy">
          <h3>${esc(p.title)}</h3>
          <p>${esc(p.body)}</p>
        </div>
        <div class="ub-pillar__visual">
          ${p.panel()}
        </div>
      </article>`,
    )
    .join('');

  const extensionCards = c.extensions
    .map(
      (ex) => `
      <article class="ub-ext reveal">
        <div class="ub-ext__copy">
          <h3>${esc(ex.title)}</h3>
          <p>${esc(ex.body)}</p>
        </div>
        <div class="ub-ext__visual">${ex.panel()}</div>
      </article>`,
    )
    .join('');

  return `
    <section class="ub-hero unite-go-hero" id="top">
      <div class="ub-hero__inner">
        <h1 class="ub-hero__title reveal">${esc(c.heroTitle)}</h1>
        <p class="ub-hero__subtitle reveal">${esc(c.heroSubtitle)}</p>
        <div class="ub-hero__actions reveal">
          <a class="ub-btn ub-btn--primary" href="#contact">${esc(c.ctaPrimary)}</a>
          <a class="ub-btn ub-btn--outline" href="#pillars">${esc(c.ctaSecondary)}</a>
        </div>
      </div>
    </section>

    <section class="ub-contrast" id="compare">
      <div class="ub-contrast__inner">
        <article class="ub-contrast__card ub-contrast__card--past reveal">
          ${ugContrastPastSvg()}
          <span class="ub-contrast__label">${esc(c.contrastPastLabel)}</span>
          <h2>${esc(c.contrastPastTitle)}</h2>
          <ul>
            ${c.contrastPastItems.map((item) => `<li>${esc(item.text)}</li>`).join('')}
          </ul>
        </article>
        <article class="ub-contrast__card ub-contrast__card--now reveal" style="transition-delay:80ms">
          ${ugContrastNowSvg()}
          <span class="ub-contrast__label">${esc(c.contrastNowLabel)}</span>
          <h2>${esc(c.contrastNowTitle)}</h2>
          <ul>
            ${c.contrastNowItems.map((item) => `<li>${esc(item.text)}</li>`).join('')}
          </ul>
        </article>
      </div>
    </section>

    <section class="ub-pillars section" id="pillars">
      <div class="section__inner ub-pillars__wrap">
        <header class="ub-pillars__head reveal">
          <p class="section__eyebrow">${esc(c.pillarsEyebrow)}</p>
          <h2 class="section__title section__title--center">${esc(c.pillarsTitle)}</h2>
        </header>
        <div class="ub-pillars__layout">
          <div class="ub-pillars__line" aria-hidden="true">
            <span class="ub-pillars__line-fill unite-go-pillars__line-fill"></span>
          </div>
          <div class="ub-pillars__list">
            ${pillarRows}
          </div>
        </div>
      </div>
    </section>

    <section class="ub-ext section" id="extensions">
      <div class="section__inner">
        <header class="ub-ext__head reveal">
          <p class="section__eyebrow">${esc(c.extensionsEyebrow)}</p>
          <h2 class="section__title section__title--center">${esc(c.extensionsTitle)}</h2>
        </header>
        <div class="ub-ext__grid">
          ${extensionCards}
        </div>
      </div>
    </section>

    <section class="ub-closing section unite-go-closing" id="closing">
      <div class="section__inner">
        <div class="ub-closing__panel reveal">
          <h2 class="ub-closing__title">${esc(c.closingTitle)}</h2>
          <p class="ub-closing__subtitle">${esc(c.closingSubtitle)}</p>
          <div class="ub-closing__actions">
            <a class="ub-btn ub-btn--primary ub-btn--lg" href="#contact">${esc(c.closingCta)}</a>
          </div>
        </div>
      </div>
    </section>
  `;
}
